import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { API } from "aws-amplify";
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  InputLabel,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { DialogWindow } from "UI/DialogWindow";
import { FormTextField } from "UI/FormTextField";
import { FormSelectField } from "UI/FormSelectField";
import { phoneNumberValidation, required, zipCodeValidation } from "models/validations";
import { states } from "models/states";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { RequiredMark } from "UI/RequiredMark";
import { FormMoneyField } from "UI/FormMoneyField";
import { useCloseAndReset } from "hooks/useCloseAndReset";
import { FormCheckbox } from "UI/FormCheckbox";
import { useAuthenticator } from "auth/useAuthenticator";

/** @type {import("types/commonTypes").NewOrganizationData}*/
const defaultFormData = {
  name: "",
  street: "",
  street2: "",
  city: "",
  state: "",
  zipCode: "",
  phone: "",
  email: "",
  website: "",
  speciality: "",
  notification_price: 0,
  question_price: 0,
  referral_price: 0,
  button_device_price: 0,
  number_of_free_clicks: 0,
  isFlowSimplified: true,
};

export const AddNewRecipientDialog = ({
  open,
  handleClose,
  setRicipentId,
  setRecipientName,
}) => {
  const { handleAuthError } = useAuthenticator();
  const dispatch = useAppDispatch();
  const { createApiCall } = useCreateApiCall();
  const isLoading = useAppSelector((state) => !!state.spinner);
  const [defaultContract, setDefaultContract] = React.useState(null);

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    setValue,
    formState: { isDirty },
  } = useForm({
    defaultValues: defaultFormData,
  });

  const onSubmit = createApiCall(async () => {
    const organization = getValues();
    const result = await API.put("restApi", "/recipients", {
      body: {
        organization,
      },
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    setRicipentId(result?.id);
    setRecipientName(result?.name);
  });

  const closeAndReset = useCloseAndReset(() => {
    reset(defaultFormData);
    handleClose();
  }, isDirty);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const loadDefaultContract = async () => {
      try {
        const result = await API.post("restApi", `/recipients`, {
          body: {
            action: "get_default_contract",
          },
        });
        if (result?.error) {
          console.error(result.error);
          dispatch(showError(result.error));
          return;
        }
        setDefaultContract(result);
      } catch (err) {
        handleAuthError(err);
      }
    };

    loadDefaultContract();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!defaultContract) return;

    setValue("notification_price", defaultContract?.notification_price ?? 0);
    setValue("question_price", defaultContract?.question_price ?? 0);
    setValue("referral_price", defaultContract?.referral_price ?? 0);
    setValue("button_device_price", defaultContract?.button_device_price ?? 0);
    setValue("number_of_free_clicks", defaultContract?.number_of_free_clicks ?? 0);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultContract]);

  return (
    <DialogWindow
      open={open}
      withCloseButton
      handleClose={closeAndReset}
      title={"Add New Recipient"}
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack direction={fullScreen ? "column" : "row"} gap={3} mt={2}>
            <Stack width="100%" gap={1}>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="name">
                  Name <RequiredMark />
                </InputLabel>
                <FormTextField
                  id="name"
                  name="name"
                  control={control}
                  rules={{ required }}
                  InputProps={{
                    autoComplete: "off",
                  }}
                  disabled={isLoading}
                />
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="street">
                  Address Line 1 <RequiredMark />
                </InputLabel>
                <FormTextField
                  id="street"
                  name="street"
                  control={control}
                  rules={{ required }}
                  InputProps={{
                    autoComplete: "off",
                  }}
                  disabled={isLoading}
                />
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="street22">Address Line 2</InputLabel>
                <FormTextField
                  id="stree2"
                  name="street2"
                  control={control}
                  InputProps={{
                    autoComplete: "off",
                  }}
                  disabled={isLoading}
                />
              </Stack>
              <Stack direction={"row"} gap={1}>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="city">
                    City <RequiredMark />
                  </InputLabel>
                  <FormTextField
                    id="city"
                    name="city"
                    control={control}
                    rules={{ required }}
                    InputProps={{
                      autoComplete: "off",
                    }}
                    disabled={isLoading}
                  />
                </Stack>
                <Stack alignItems={"flex-start"} maxWidth={"35%"}>
                  <InputLabel htmlFor="state">
                    State <RequiredMark />
                  </InputLabel>
                  <FormSelectField
                    id="state"
                    name="state"
                    control={control}
                    options={states}
                    rules={{ required }}
                    disabled={isLoading}
                  />
                </Stack>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="zipCode">
                    Zip Code <RequiredMark />
                  </InputLabel>
                  <FormTextField
                    id="zipCode"
                    name="zipCode"
                    control={control}
                    rules={zipCodeValidation}
                    InputProps={{
                      autoComplete: "off",
                    }}
                    disabled={isLoading}
                  />
                </Stack>
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="phone">
                  Phone <RequiredMark />
                </InputLabel>
                <FormMaskedTextField
                  id="phone"
                  name="phone"
                  control={control}
                  rules={phoneNumberValidation}
                  mask="999-999-9999"
                  InputProps={{
                    autoComplete: "off",
                  }}
                  disabled={isLoading}
                />
              </Stack>
            </Stack>
            <Stack width="100%" gap={1}>
              {/* <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="email">
                  Email <RequiredMark />
                </InputLabel>
                <FormTextField
                  id="email"
                  name="email"
                  control={control}
                  rules={emailValidation}
                  InputProps={{
                    autoComplete: "off",
                  }}
                />
              </Stack> */}
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="website">Speciality (optional)</InputLabel>
                <FormTextField
                  id="speciality"
                  name="speciality"
                  control={control}
                  disabled={isLoading}
                />
              </Stack>
              <Stack mb={2} alignItems={"flex-start"}>
                <InputLabel htmlFor="website">Website (optional)</InputLabel>
                <FormTextField
                  id="website"
                  name="website"
                  control={control}
                  InputProps={{
                    autoComplete: "off",
                  }}
                  disabled={isLoading}
                />
              </Stack>
              <Divider />
              <Stack mt={1} direction={"row"} gap={1}>
                <Stack alignItems={"flex-start"} flex={1}>
                  <InputLabel htmlFor="notification_price">
                    Notification
                  </InputLabel>
                  <FormMoneyField
                    id="notification_price"
                    name="notification_price"
                    control={control}
                    InputProps={{
                      autoComplete: "new-password",
                      inputProps: {
                        min: 0,
                        max: 10000,
                        step: 1,
                      },
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Stack>
                <Stack alignItems={"flex-start"} flex={1}>
                  <InputLabel htmlFor="question_price">Question</InputLabel>
                  <FormMoneyField
                    id="question_price"
                    name="question_price"
                    control={control}
                    InputProps={{
                      autoComplete: "new-password",
                      inputProps: {
                        min: 0,
                        max: 10000,
                        step: 1,
                      },
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Stack>
                <Stack alignItems={"flex-start"} flex={1}>
                  <InputLabel htmlFor="referral_price">Referral</InputLabel>
                  <FormMoneyField
                    id="referral_price"
                    name="referral_price"
                    control={control}
                    InputProps={{
                      autoComplete: "new-password",
                      inputProps: {
                        min: 0,
                        max: 10000,
                        step: 1,
                      },
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              </Stack>
              <Stack direction={"row"} flexWrap={"wrap"} gap={1}>
                <Stack alignItems={"flex-start"} flex={1}>
                  <InputLabel htmlFor="button_device_price">
                    Button Device
                  </InputLabel>
                  <FormMoneyField
                    id="button_device_price"
                    name="button_device_price"
                    control={control}
                    InputProps={{
                      autoComplete: "new-password",
                      inputProps: {
                        min: 0,
                        max: 10000,
                        step: 1,
                      },
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    disabled={!defaultContract}
                  />
                </Stack>
                <Stack alignItems={"flex-start"} flex={1}>
                  <InputLabel htmlFor="number_of_free_clicks">
                    Number Of Free Clicks
                  </InputLabel>
                  <FormMoneyField
                    id="number_of_free_clicks"
                    name="number_of_free_clicks"
                    control={control}
                    InputProps={{
                      autoComplete: "new-password",
                      inputProps: {
                        min: 0,
                        max: 10000,
                        step: 1,
                      },
                    }}
                  />
                </Stack>
              </Stack>
              <Stack alignItems={"flex-start"}>
                <FormCheckbox
                  name="isInvoicingDisabled"
                  control={control}
                  label={"Automatic invoicing disabled."}
                />
              </Stack>
            </Stack>
          </Stack>
        </form>
      }
      actions={
        <Stack
          direction={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"100%"}
          gap={2}
          mb={2}
        >
          <Box ml={2} sx={{ color: "gray", fontSize: 14 }}>
            <RequiredMark /> These fields are required
          </Box>
          <Button variant="contained" onClick={handleSubmit(onSubmit)}>
            Add New Recipient
          </Button>
        </Stack>
      }
    />
  );
};
